import {
  cloneElement,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from "react";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  rightSlot?: ReactNode;
  /** When true, shows a compact ✨ AI badge (merged with rightSlot). */
  aiFilled?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  rightSlot,
  aiFilled,
  className,
  children,
}: FieldProps) {
  const autoId = useId();
  const effectiveId = htmlFor ?? autoId;
  const showRight = aiFilled || rightSlot;

  // When the call site didn't pass an explicit htmlFor, thread the generated id
  // onto the single child so the <label> binds to the form control. Children
  // that already declare their own id are left alone.
  let renderedChildren: ReactNode = children;
  if (!htmlFor && isValidElement(children)) {
    const child = children as ReactElement<{ id?: string }>;
    if (!child.props.id) {
      renderedChildren = cloneElement(child, { id: effectiveId });
    }
  }

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={effectiveId}
          className="text-xs font-medium text-neutral-700 tracking-wide uppercase"
        >
          {label}
          {required && <span className="text-danger-700 ml-0.5">*</span>}
        </label>
        {showRight ? (
          <span className="flex items-center gap-1.5 shrink-0">
            {aiFilled ? (
              <Badge tone="accent" className="text-[10px] !py-0 !px-1.5">
                ✨ AI
              </Badge>
            ) : null}
            {rightSlot ? <span className="text-xs text-neutral-500">{rightSlot}</span> : null}
          </span>
        ) : null}
      </div>
      {renderedChildren}
      {error ? (
        <span className="text-xs text-danger-700">{error}</span>
      ) : hint ? (
        <span className="text-xs text-neutral-500">{hint}</span>
      ) : null}
    </div>
  );
}
